import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateLeadGenerationSurveyTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const departmentId = uuidv4();
  const departmentBlockId = uuidv4();
  const importanceId = uuidv4();
  const importanceBlockId = uuidv4();
  const captureMethodId = uuidv4();
  const captureMethodBlockId = uuidv4();
  const generationMethodId = uuidv4();
  const generationMethodBlockId = uuidv4();
  const channelsId = uuidv4();
  const channelsBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const marketingOptionId = uuidv4();
  const salesOptionId = uuidv4();
  const crmEntryOptionId = uuidv4();
  const leadFormsOptionId = uuidv4();
  const automationOptionId = uuidv4();
  const contentOptionId = uuidv4();
  const webinarsOptionId = uuidv4();
  const quizzesOptionId = uuidv4();
  const socialMediaOptionId = uuidv4();
  const ppcOptionId = uuidv4();
  const emailMarketingOptionId = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const captureMethodMediaId = uuidv4();

  return {
    ...themes.spicedCocoaPeach,
    "name": "Lead Generation Survey",
    "overview": "Help us understand your customer acquisition challenges",
    "about": "Share insights about your growth strategies and get personalized recommendations",
    "tone": "Casual + Professional",
    "fontSizeSetting": "SMALL",
    "hideBuiltWithBadge": false,
    "closed": false,
    "collectionId": collectionId,
    "closedMessage": null,
    "legalDisclaimer": false,
    "legalMessage": null,
    "notifications": false,
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "notificationEmail": null,
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "skipWelcomeScreen": false,
    "googleTagManager": null,
    "metaPixel": null,
    "hiddenFields": [],
    "elementType": "SQUARED",
    "journeyItems": [
      {
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "order": 0,
        "type": "WELCOME_SCREEN",
        "content": "What's your biggest customer acquisition challenge?",
        "secondaryContent": "Complete our growth strategy survey and get a free consultation with our customer success team.",
        "thirdContent": "Begin assessment",
        "forthContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": {
          "id": welcomeMediaId,
          "type": "image",
          "name": "photo-1521737711867-e3b97375f902",
          "link": "https://images.unsplash.com/photo-1521737711867-e3b97375f902?fit=crop&q=80&w=774",
          "provider": "UNSPLASH",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
        },
        "scoreValues": null,
        "selectOptions": null,
        "additionalSettings": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "logicItems": null,
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "order": 1,
        "content": "Let's start with your name?",
        "secondaryContent": null,
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": null,
        "scoreValues": null,
        "selectOptions": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "type": "CONTACT_INFO",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": emailId,
        "blockId": emailBlockId,
        "order": 2,
        "content": "What's your work email?",
        "secondaryContent": "We'll send your personalized growth recommendations here.",
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": null,
        "scoreValues": null,
        "selectOptions": null,
        "additionalSettings": null,
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "type": "EMAIL",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": departmentId,
        "blockId": departmentBlockId,
        "order": 3,
        "content": `What's your role, {{${contactInfoBlockId}__first_name}}?`,
        "secondaryContent": null,
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": null,
        "scoreValues": null,
        "selectOptions": [
          {
            "id": marketingOptionId,
            "order": 0,
            "value": "Growth Marketing"
          },
          {
            "id": salesOptionId,
            "order": 1,
            "value": "Sales & Business Development"
          }
        ],
        "additionalSettings": null,
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "type": "DROPDOWN",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": importanceId,
        "blockId": importanceBlockId,
        "order": 4,
        "content": `How critical is customer acquisition for your {{${departmentBlockId}}} team?`,
        "secondaryContent": null,
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": 1,
        "maxRange": 6,
        "minLabel": "Not a priority",
        "maxLabel": "Top priority",
        "media": null,
        "scoreValues": null,
        "selectOptions": null,
        "additionalSettings": null,
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "type": "OPTION_SCALE",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": captureMethodId,
        "blockId": captureMethodBlockId,
        "order": 5,
        "content": "How does your team currently track potential customers?",
        "secondaryContent": null,
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": {
          "id": captureMethodMediaId,
          "type": "image",
          "name": "photo-1556761175-b413da4baf72",
          "link": "https://images.unsplash.com/photo-1556761175-b413da4baf72?fit=crop&q=80&w=900",
          "provider": "UNSPLASH",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
        },
        "scoreValues": null,
        "selectOptions": [
          {
            "id": crmEntryOptionId,
            "order": 0,
            "value": "Spreadsheets & manual tracking"
          },
          {
            "id": leadFormsOptionId,
            "order": 1,
            "value": "Website forms & landing pages"
          },
          {
            "id": automationOptionId,
            "order": 2,
            "value": "CRM & automation platforms"
          }
        ],
        "additionalSettings": null,
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "logicItems": null,
        "type": "MULTIPLE_CHOICE",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": generationMethodId,
        "blockId": generationMethodBlockId,
        "order": 6,
        "content": "What's your primary method for attracting new customers?",
        "secondaryContent": null,
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": null,
        "scoreValues": null,
        "selectOptions": [
          {
            "id": contentOptionId,
            "order": 0,
            "value": "Content marketing & SEO"
          },
          {
            "id": webinarsOptionId,
            "order": 1,
            "value": "Webinars & demos"
          },
          {
            "id": quizzesOptionId,
            "order": 2,
            "value": "Interactive tools & assessments"
          }
        ],
        "additionalSettings": null,
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "type": "MULTIPLE_CHOICE",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": channelsId,
        "blockId": channelsBlockId,
        "order": 7,
        "content": `Which channels drive the most qualified prospects for your {{${departmentBlockId}}} team?`,
        "secondaryContent": null,
        "thirdContent": null,
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": null,
        "scoreValues": null,
        "selectOptions": [
          {
            "id": socialMediaOptionId,
            "order": 0,
            "value": "LinkedIn & social selling"
          },
          {
            "id": ppcOptionId,
            "order": 1,
            "value": "Google Ads & paid search"
          },
          {
            "id": emailMarketingOptionId,
            "order": 2,
            "value": "Email campaigns & nurturing"
          }
        ],
        "additionalSettings": null,
        "forthContent": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "type": "MULTIPLE_CHOICE",
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      },
      {
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "order": 9,
        "type": "END_SCREEN",
        "content": `Thanks for sharing your insights, {{${contactInfoBlockId}__first_name}}!`,
        "secondaryContent": null,
        "thirdContent": "Get my recommendations",
        "forthContent": "https://www.formsuite.co",
        "manditory": false,
        "internalName": null,
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "multiSelect": null,
        "youtubeVideoURL": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "media": null,
        "scoreValues": null,
        "selectOptions": null,
        "additionalSettings": null,
        "icon": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "logicItems": null,
        "formId": formId,
        "createdAt": new Date(),
        "updatedAt": new Date()
      }
    ],
    "snippets": [],
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateLeadGenerationSurveyTemplate;